import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, 
  MapPin, 
  Trophy, 
  Target, 
  Users,
  Award,
  Calendar,
  Video,
  MessageSquare,
  Star
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CoachProfilePage = () => {
  const { coachId } = useParams();
  const [coach, setCoach] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetchCoach();
  }, [coachId]);

  const fetchCoach = async () => {
    try {
      setError(false);
      const response = await axios.get(`${API_URL}/api/coaches/${coachId}`);
      setCoach(response.data);
    } catch (err) {
      console.error('Failed to fetch coach:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading coach profile...</p>
        </div>
      </div>
    );
  }

  if (error || !coach) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Users className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-display text-xl uppercase mb-2">Coach Not Found</h3>
          <p className="text-muted-foreground mb-4">This coach profile doesn't exist or has been removed.</p>
          <Link to="/coaches">
            <Button variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Coaches
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24 md:pb-8">
      {/* Hero Section with Coach Photo */}
      <div className="relative">
        {/* Background gradient/pattern */}
        <div className="absolute inset-0 h-80 bg-gradient-to-br from-primary/20 via-secondary/10 to-background" />
        
        <div className="relative max-w-6xl mx-auto px-4 pt-8">
          <Link 
            to="/coaches" 
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Coaches
          </Link>

          <div className="flex flex-col lg:flex-row gap-8 items-start">
            {/* Coach Photo & Logo */}
            <div className="flex-shrink-0">
              <div className="relative">
                <div className="w-64 h-64 md:w-80 md:h-80 rounded-2xl overflow-hidden shadow-2xl border-4 border-background">
                  {coach.photo_url ? (
                    <img 
                      src={coach.photo_url} 
                      alt={coach.name}
                      className="w-full h-full object-cover"
                      style={{
                        objectPosition: coach.photo_position?.objectPosition || 'center'
                      }}
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-primary/30 to-secondary/30 flex items-center justify-center">
                      <span className="font-display font-bold text-8xl text-primary/50">
                        {coach.name?.charAt(0)?.toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>
                {/* Logo overlay */}
                {coach.logo_url && (
                  <div className="absolute -bottom-4 -right-4 w-20 h-20 bg-background rounded-xl shadow-lg p-2">
                    <img 
                      src={coach.logo_url} 
                      alt={`${coach.name} logo`}
                      className="w-full h-full object-contain"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Coach Info */}
            <div className="flex-1 pt-4">
              <div className="flex items-center gap-3 mb-2">
                {coach.region && (
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <MapPin className="w-4 h-4" />
                    <span className="text-sm">{coach.region}</span>
                  </div>
                )}
              </div>
              
              <h1 className="font-display font-black text-4xl md:text-5xl lg:text-6xl uppercase tracking-tight text-foreground mb-4">
                {coach.name}
              </h1>

              {coach.tagline && (
                <p className="text-xl text-muted-foreground italic mb-4">
                  "{coach.tagline}"
                </p>
              )}

              {/* Disciplines */}
              {coach.disciplines?.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-6">
                  {coach.disciplines.map(d => (
                    <Badge key={d} className="text-sm px-3 py-1 bg-primary/10 text-primary border-primary/20">
                      {d}
                    </Badge>
                  ))}
                </div>
              )}

              {/* Quick Stats - always show, with friendly placeholders for zeros */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                {/* Years Experience */}
                <div className="text-center p-3 bg-muted/50 rounded-lg">
                  <Calendar className="w-5 h-5 mx-auto mb-1 text-primary" />
                  {coach.years_experience && coach.years_experience > 0 ? (
                    <>
                      <p className="font-bold text-lg">{coach.years_experience}+</p>
                      <p className="text-xs text-muted-foreground">Years Experience</p>
                    </>
                  ) : (
                    <>
                      <p className="font-bold text-sm leading-tight">Just Getting</p>
                      <p className="text-xs text-muted-foreground">Started!</p>
                    </>
                  )}
                </div>

                {/* Students Helped */}
                <div className="text-center p-3 bg-muted/50 rounded-lg">
                  <Users className="w-5 h-5 mx-auto mb-1 text-primary" />
                  {coach.students_helped && coach.students_helped > 0 ? (
                    <>
                      <p className="font-bold text-lg">{coach.students_helped}+</p>
                      <p className="text-xs text-muted-foreground">Students Helped</p>
                    </>
                  ) : (
                    <>
                      <p className="font-bold text-sm leading-tight">Ready to</p>
                      <p className="text-xs text-muted-foreground">Help You!</p>
                    </>
                  )}
                </div>

                {/* Critiques Done */}
                <div className="text-center p-3 bg-muted/50 rounded-lg">
                  <Video className="w-5 h-5 mx-auto mb-1 text-primary" />
                  {coach.critiques_completed && coach.critiques_completed > 0 ? (
                    <>
                      <p className="font-bold text-lg">{coach.critiques_completed}+</p>
                      <p className="text-xs text-muted-foreground">Critiques Done</p>
                    </>
                  ) : (
                    <>
                      <p className="font-bold text-sm leading-tight">In the Middle</p>
                      <p className="text-xs text-muted-foreground">of a Critique!</p>
                    </>
                  )}
                </div>

                {/* Rating */}
                <div className="text-center p-3 bg-muted/50 rounded-lg">
                  <Star className="w-5 h-5 mx-auto mb-1 text-amber-500" />
                  {coach.rating && coach.rating > 0 ? (
                    <>
                      <p className="font-bold text-lg">{coach.rating}</p>
                      <p className="text-xs text-muted-foreground">Rating</p>
                    </>
                  ) : (
                    <>
                      <p className="font-bold text-sm leading-tight">Be the First</p>
                      <p className="text-xs text-muted-foreground">to Rate!</p>
                    </>
                  )}
                </div>
              </div>

              {/* CTA Button */}
              <Link to="/critique/new">
                <Button size="lg" className="rounded-full px-8">
                  <MessageSquare className="w-5 h-5 mr-2" />
                  Get Coached by {coach.name?.split(' ')[0]}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Bio & Story */}
          <div className="lg:col-span-2 space-y-8">
            {/* About Section */}
            <Card>
              <CardContent className="p-6 md:p-8">
                <h2 className="font-display font-bold text-2xl uppercase tracking-wide mb-4 flex items-center gap-2">
                  <Target className="w-6 h-6 text-primary" />
                  About {coach.name?.split(' ')[0]}
                </h2>
                <div className="prose prose-lg max-w-none text-muted-foreground">
                  {coach.full_bio ? (
                    <div dangerouslySetInnerHTML={{ __html: coach.full_bio.replace(/\n/g, '<br />') }} />
                  ) : coach.bio ? (
                    <p>{coach.bio}</p>
                  ) : (
                    <p>More details coming soon...</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Coaching Philosophy */}
            {coach.coaching_philosophy && (
              <Card>
                <CardContent className="p-6 md:p-8">
                  <h2 className="font-display font-bold text-2xl uppercase tracking-wide mb-4">
                    Coaching Philosophy
                  </h2>
                  <p className="text-lg text-muted-foreground italic">
                    "{coach.coaching_philosophy}"
                  </p>
                </CardContent>
              </Card>
            )}

            {/* What You'll Learn */}
            {coach.specialties?.length > 0 && (
              <Card>
                <CardContent className="p-6 md:p-8">
                  <h2 className="font-display font-bold text-2xl uppercase tracking-wide mb-4">
                    What You'll Learn
                  </h2>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {coach.specialties.map((specialty, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Target className="w-4 h-4 text-primary" />
                        </div>
                        <span className="text-sm">{specialty}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column - Achievements & Info */}
          <div className="space-y-6">
            {/* Achievements */}
            {coach.achievements?.length > 0 && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-display font-bold text-lg uppercase tracking-wide mb-4 flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-amber-500" />
                    Achievements
                  </h3>
                  <div className="space-y-3">
                    {coach.achievements.map((achievement, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <Award className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm">{achievement}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Contact/Social */}
            {(coach.website || coach.instagram || coach.facebook) && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-display font-bold text-lg uppercase tracking-wide mb-4">
                    Connect
                  </h3>
                  <div className="space-y-3">
                    {coach.website && (
                      <a 
                        href={coach.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 text-sm text-muted-foreground hover:text-primary transition-colors"
                      >
                        <span className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">🌐</span>
                        Website
                      </a>
                    )}
                    {coach.instagram && (
                      <a 
                        href={`https://instagram.com/${coach.instagram}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 text-sm text-muted-foreground hover:text-primary transition-colors"
                      >
                        <span className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">📸</span>
                        @{coach.instagram}
                      </a>
                    )}
                    {coach.facebook && (
                      <a 
                        href={coach.facebook} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 text-sm text-muted-foreground hover:text-primary transition-colors"
                      >
                        <span className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">👤</span>
                        Facebook
                      </a>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* CTA Card */}
            <Card className="bg-primary/5 border-primary/20">
              <CardContent className="p-6 text-center">
                <h3 className="font-display font-bold text-lg uppercase mb-2">
                  Ready to Improve?
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Submit your video and get personalized feedback from {coach.name?.split(' ')[0]}.
                </p>
                <Link to="/critique/new">
                  <Button className="w-full rounded-full">
                    Submit a Critique
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Media Gallery Section */}
        {coach.media_gallery && coach.media_gallery.length > 0 && (
          <div className="mt-12">
            <h2 className="font-display font-bold text-2xl uppercase tracking-wide mb-6 text-center flex items-center justify-center gap-2">
              <Video className="w-6 h-6 text-primary" />
              Training Highlights
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {coach.media_gallery.slice(0, 3).map((media, idx) => (
                <Card key={idx} className="overflow-hidden group">
                  <div className="aspect-video relative bg-muted">
                    {media.type === 'video' ? (
                      <>
                        {media.url.includes('youtube.com') || media.url.includes('youtu.be') ? (
                          <iframe
                            src={`https://www.youtube.com/embed/${media.url.includes('youtu.be') 
                              ? media.url.split('/').pop() 
                              : new URL(media.url).searchParams.get('v')}`}
                            className="w-full h-full"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        ) : (
                          <video 
                            src={media.url} 
                            className="w-full h-full object-cover"
                            controls
                            poster={media.thumbnail}
                          />
                        )}
                      </>
                    ) : (
                      <img 
                        src={media.url} 
                        alt={media.caption || `Gallery image ${idx + 1}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        style={{
                          objectPosition: media.position?.objectPosition || 'center'
                        }}
                      />
                    )}
                  </div>
                  {media.caption && (
                    <CardContent className="p-4">
                      <p className="text-sm text-muted-foreground">{media.caption}</p>
                    </CardContent>
                  )}
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CoachProfilePage;
